import React, { useEffect, useId, useRef, useState } from 'react';
import { propertyAPI } from '../../services/api';
import { apiErrorMessage, fullAddress } from './propertyRegistry';

export default function PropertyParentPicker({ tenantSlug, value, onChange, parentKind, disabled, required, excludeUid }) {
  const id = useId();
  const sequence = useRef(0);
  const [query, setQuery] = useState('');
  const [selectedLabel, setSelectedLabel] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!value) setSelectedLabel('');
    else if (!selectedLabel) setSelectedLabel(value);
  }, [selectedLabel, value]);

  useEffect(() => {
    if (disabled || value || query.trim().length < 2) {
      setResults([]);
      setSearched(false);
      return undefined;
    }
    const current = ++sequence.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const response = await propertyAPI.getAll(tenantSlug, {
          search: query.trim(),
          property_kind: parentKind,
          status: 'active',
          page: 1,
          limit: 10,
        });
        if (current !== sequence.current) return;
        setResults((response.data.properties || []).filter(item => item.property_uid !== excludeUid));
        setSearched(true);
      } catch (requestError) {
        if (current !== sequence.current) return;
      setError(apiErrorMessage(requestError, 'पालक मालमत्ता शोधता आली नाही.'));
        setResults([]);
        setSearched(true);
      } finally {
        if (current === sequence.current) setLoading(false);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [disabled, excludeUid, parentKind, query, tenantSlug, value]);

  const choose = property => {
    onChange(property.property_uid);
    setSelectedLabel(`${property.property_name} · ${property.assessment_number} · ${fullAddress(property)}`);
    setQuery('');
    setResults([]);
    setSearched(false);
  };

  return (
    <div className="property-party-picker">
      <label htmlFor={`${id}-parent`}>
        पालक {parentKind === 'parcel' ? 'भूखंड' : 'इमारत'}{required ? ' *' : ''}
      </label>
      {value ? (
        <div className="property-party-selected">
          <span><strong>{selectedLabel || value}</strong><small>{value}</small></span>
          {!disabled && <button type="button" className="property-icon-button" onClick={() => { onChange(''); setSelectedLabel(''); }} aria-label="Clear parent property">×</button>}
        </div>
      ) : (
        <div className="property-combobox-wrap">
          <input
            id={`${id}-parent`}
            type="search"
            role="combobox"
            aria-expanded={searched || Boolean(error)}
            aria-controls={`${id}-parent-results`}
            value={query}
            disabled={disabled}
            required={required}
            onChange={event => setQuery(event.target.value)}
        placeholder="क्रमांक किंवा पत्ता शोधा"
            autoComplete="off"
          />
      {loading && <span className="property-inline-loader" role="status">शोधत आहे…</span>}
          {(searched || error) && (
            <div id={`${id}-parent-results`} className="property-combobox-results" role="listbox">
              {error && <p className="property-combobox-message" role="alert">{error}</p>}
        {!error && !loading && results.length === 0 && <p className="property-combobox-message">जुळणारी पालक मालमत्ता सापडली नाही.</p>}
              {results.map(property => (
                <button type="button" role="option" aria-selected="false" key={property.property_uid} onClick={() => choose(property)}>
                  <strong>{property.property_name}</strong>
                  <small>{property.assessment_number}</small>
                  <span>{fullAddress(property)}</span>
                  <small>{property.property_uid}</small>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
